import { useToast } from '@chakra-ui/react';
import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  EquipmentTypeNamePreview,
  EquipmentTypeView,
  Pagination,
  Parametros,
  SelectItem,
} from '~/types';
import { queryClient } from '~/utils/react-query';
import {
  createEquipmentType,
  getEquipmentTypePreview,
  getEquipmentTypes,
  getEquipmentTypesAsSelect,
  getEquipmentTypesPreview,
  updateEquipmentTypeById,
} from '../../services';
import { CreatePayload, UpdatePayload } from './types';

export function useEquipmentTypes(
  page: number,
  size = 6,
  options: UseQueryOptions<Pagination<EquipmentTypeView>, unknown> = {},
): UseQueryResult<Pagination<EquipmentTypeView>, unknown> {
  return useQuery<Pagination<EquipmentTypeView>, unknown>(
    ['equipmentTypes', page],
    () => getEquipmentTypes(page, size),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useUpdateEquipmentType(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  UpdatePayload,
  unknown
> {
  const toast = useToast();

  return useMutation(
    async ({ id, formData }: UpdatePayload) =>
      updateEquipmentTypeById(id, formData),
    {
      onSuccess: () => {
        toast({
          title: 'Alterado com sucesso',
          description: 'Dados alterados com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('equipmentTypes');
        queryClient.invalidateQueries('equipmentModels');
        queryClient.invalidateQueries('equipments');
        queryClient.invalidateQueries('equipmentTypesSelect');
      },
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao atualizar dados, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useCreateEquipmentType(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  CreatePayload,
  unknown
> {
  const toast = useToast();

  return useMutation(
    async (data: CreatePayload) => createEquipmentType(data.formData),
    {
      onSuccess: () => {
        toast({
          title: `Tipo criado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('equipmentTypes');
        queryClient.invalidateQueries('equipmentTypesSelect');
      },
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao criar tipo, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useEquipmentTypesAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
): UseQueryResult<SelectItem[], unknown> {
  return useQuery<SelectItem[], unknown>(
    ['equipmentTypesSelect'],
    () => getEquipmentTypesAsSelect(),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useEquipmentTypesPreview(
  parametros: Parametros[],
  id: number,
  options: UseQueryOptions<EquipmentTypeNamePreview> = {},
): UseQueryResult<EquipmentTypeNamePreview> {
  return useQuery<EquipmentTypeNamePreview>(
    ['equipmentTypesPreview', id],
    () => getEquipmentTypesPreview(parametros, id),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useEquipmentTypePreview(
  id: number,
  options: UseQueryOptions<string> = {},
): UseQueryResult<string> {
  const toast = useToast();
  return useQuery<string>(
    ['equipmentTypePreview', id],
    () => getEquipmentTypePreview(id),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Não foi possível restaurar a descrição do equipamento.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}
